import { useState } from "react";
import { Check, ArrowRight, AlertTriangle, Loader2 } from "lucide-react";
import { COLORS } from "../styles/tokens";
import { PACKAGES } from "../data/packages";
import { ApiError } from "../lib/api";
import type { OnboardingPayload, PackageId } from "../types/realsynq";

/** Turn a thrown submission error into a human-readable line for the form. */
function submitErrorMessage(err: unknown): string {
  // ApiError carries the server's RFC7807 `detail` when the API supplied one.
  if (err instanceof ApiError && err.detail) return err.detail;
  if (err instanceof ApiError) {
    return err.status >= 500
      ? "Our server had trouble saving your request. Please try again in a moment."
      : "That request couldn't be accepted. Please check your details and try again.";
  }
  // Network failure, timeout/abort, or anything else.
  return "We couldn't reach the server. Check your connection and try again.";
}

interface OnboardingViewProps {
  onBack: () => void;
  isMobile: boolean;
  /** Optional submission handler — defaults to no-op stub. Wire to /api/onboarding in production. */
  onSubmit?: (payload: OnboardingPayload) => Promise<void> | void;
}

export function OnboardingView({ onBack, isMobile, onSubmit }: OnboardingViewProps) {
  const [form, setForm] = useState<OnboardingPayload>({
    company: "",
    contact: "",
    email: "",
    dsp: "",
    seats: "",
    packages: [],
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const togglePkg = (id: PackageId) => {
    setForm(f => ({
      ...f,
      packages: f.packages.includes(id) ? f.packages.filter(p => p !== id) : [...f.packages, id],
    }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(form);
      }
      setSubmitted(true);
    } catch (err) {
      // Surface the failure instead of silently swallowing it — the user
      // keeps their filled-in form and can retry.
      setError(submitErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        style={{
          padding: isMobile ? "80px 16px" : "120px 32px",
          maxWidth: 700,
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 12,
            background: `${COLORS.cyan}15`,
            border: `1px solid ${COLORS.cyan}40`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 24,
            boxShadow: `0 0 32px ${COLORS.cyan}33`,
          }}
        >
          <Check size={28} color={COLORS.cyan} />
        </div>
        <h2 className="display" style={{ fontSize: isMobile ? 32 : 40, fontWeight: 700, margin: "0 0 16px" }}>
          Request received.
        </h2>
        <p style={{ color: COLORS.inkDim, fontSize: 16, marginBottom: 32 }}>
          Our partnerships team will reach out within one business day with seat-mapping
          details, deal IDs, and activation timelines.
        </p>
        <button
          onClick={onBack}
          style={{
            background: "transparent",
            border: `1px solid ${COLORS.borderHi}`,
            color: COLORS.ink,
            padding: "12px 24px",
            borderRadius: 6,
            fontSize: 14,
          }}
        >
          Back to explorer
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? "40px 16px 100px" : "60px 32px", maxWidth: 880, margin: "0 auto" }}>
      <div className="mono" style={{ fontSize: 11, color: COLORS.cyan, letterSpacing: "0.15em", marginBottom: 12 }}>
        ◆ SEAT MAPPING & ONBOARDING
      </div>
      <h2
        className="display"
        style={{
          fontSize: isMobile ? "clamp(32px, 9vw, 40px)" : 44,
          fontWeight: 700,
          margin: "0 0 16px",
          lineHeight: 1,
        }}
      >
        Get a seat.<br />Get the deal IDs.
      </h2>
      <p
        style={{
          color: COLORS.inkDim,
          fontSize: isMobile ? 14 : 16,
          maxWidth: 600,
          lineHeight: 1.6,
          marginBottom: isMobile ? 32 : 48,
        }}
      >
        Tell us which DSPs you operate and which packages you're targeting. We'll map
        your seats to the appropriate deal IDs and audience keys, and ship a buying brief
        within one business day.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <Field label="Company" value={form.company} onChange={v => setForm({ ...form, company: v })} placeholder="Agency or brand name" />
        <Field label="Primary contact" value={form.contact} onChange={v => setForm({ ...form, contact: v })} placeholder="Full name" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <Field label="Work email" value={form.email} onChange={v => setForm({ ...form, email: v })} placeholder="name@company.com" type="email" />
        <Field label="Primary DSP" value={form.dsp} onChange={v => setForm({ ...form, dsp: v })} placeholder="DV360, TTD, StackAdapt..." />
      </div>

      <div style={{ marginBottom: 24 }}>
        <Field label="Seat IDs" value={form.seats} onChange={v => setForm({ ...form, seats: v })} placeholder="One per line, with platform" textarea />
      </div>

      <div style={{ marginBottom: 24 }}>
        <div className="mono" style={{ fontSize: 10, color: COLORS.inkMute, letterSpacing: "0.15em", marginBottom: 12 }}>
          PACKAGES OF INTEREST
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 8,
          }}
        >
          {PACKAGES.map(p => {
            const Icon = p.icon;
            const sel = form.packages.includes(p.id);
            return (
              <button
                key={p.id}
                onClick={() => togglePkg(p.id)}
                style={{
                  background: sel ? `${p.accent}10` : "rgba(15, 26, 44, 0.7)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${sel ? p.accent : COLORS.border}`,
                  borderRadius: 8,
                  padding: 14,
                  textAlign: "left",
                  color: COLORS.ink,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  transition: "all 0.2s",
                }}
              >
                <Icon size={16} color={sel ? p.accent : COLORS.inkDim} />
                <span style={{ fontSize: 13, fontWeight: sel ? 600 : 400 }}>{p.name}</span>
                {sel && <Check size={14} color={p.accent} style={{ marginLeft: "auto" }} />}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 32 }}>
        <Field label="Notes" value={form.notes} onChange={v => setForm({ ...form, notes: v })} placeholder="Vertical focus, campaign timing, anything else..." textarea />
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: COLORS.inkMute,
            alignSelf: isMobile ? "flex-start" : "auto",
          }}
        >
          {form.packages.length} {form.packages.length === 1 ? "PACKAGE" : "PACKAGES"} SELECTED
        </span>
        {(() => {
          const disabled = !form.company || !form.email || submitting;
          return (
            <button
              onClick={handleSubmit}
              disabled={disabled}
              aria-busy={submitting}
              style={{
                background: disabled && !submitting ? COLORS.borderHi : COLORS.cyan,
                color: disabled && !submitting ? COLORS.inkMute : COLORS.bg,
                border: "none",
                padding: isMobile ? "16px 24px" : "14px 28px",
                fontWeight: 700,
                fontSize: 14,
                borderRadius: 8,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                cursor: disabled ? (submitting ? "wait" : "not-allowed") : "pointer",
                opacity: submitting ? 0.85 : 1,
                boxShadow: disabled && !submitting ? "none" : `0 8px 24px ${COLORS.cyan}33`,
                width: isMobile ? "100%" : "auto",
              }}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="spin" /> Submitting…
                </>
              ) : (
                <>
                  Submit mapping request <ArrowRight size={16} />
                </>
              )}
            </button>
          );
        })()}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            marginTop: 20,
            padding: "12px 16px",
            background: `${COLORS.rose}12`,
            border: `1px solid ${COLORS.rose}55`,
            borderRadius: 8,
            color: COLORS.ink,
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          <AlertTriangle size={18} color={COLORS.rose} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            {error}{" "}
            <button
              onClick={handleSubmit}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: COLORS.cyan,
                fontWeight: 600,
                fontSize: 14,
                textDecoration: "underline",
              }}
            >
              Try again
            </button>
          </span>
        </div>
      )}
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  textarea?: boolean;
  type?: string;
}

function Field({ label, value, onChange, placeholder, textarea, type = "text" }: FieldProps) {
  return (
    <div>
      <div className="mono" style={{ fontSize: 10, color: COLORS.inkMute, letterSpacing: "0.15em", marginBottom: 8 }}>
        {label.toUpperCase()}
      </div>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
          style={{ width: "100%", padding: 12, borderRadius: 6, fontSize: 14, resize: "vertical" }}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ width: "100%", padding: 12, borderRadius: 6, fontSize: 14 }}
        />
      )}
    </div>
  );
}
